import { useEffect, useState } from 'react';
import LangEnum from '../../../../../../../lang/LangEnum';
import snackbarStore from '../../../../../../../stores/Snackbar.store';
import { useApplicationContext } from '../../../../../../context/ApplicationContext/ApplicationContext';
import { getWhatsAppLink } from '../../../../../../customer/utils/customer.utils';
import { useTranslation } from '../../../../../../translation/TranslationContext';
import { ordersRepository } from '../../../../../repositories/ordersRepository/orders.repository';
import { ShareLinkPanel } from '../enums/ShareLinkPanel';

interface UseOrderSharePaymentModalProps {
  orderCode: string;
  paymentUrl: string;
  orderId: string;
}

const actions = {
  [ShareLinkPanel.WHATSAPP]: {
    panel: ShareLinkPanel.WHATSAPP,
    label: LangEnum.CATALOGS_SHARE_MODAL_OPEN_WHATSAPP,
  },
  [ShareLinkPanel.COPY_LINK]: {
    panel: ShareLinkPanel.COPY_LINK,
    label: LangEnum.ORDER_CONTRACT_COPY_LINK,
  },
  [ShareLinkPanel.EMAIL]: {
    panel: ShareLinkPanel.EMAIL,
    label: LangEnum.PAYMENT_LINK_SEND_EMAIL,
  },
};

const useOrderSharePaymentModal = ({
  orderCode,
  paymentUrl,
  orderId,
}: UseOrderSharePaymentModalProps) => {
  const { state } = useApplicationContext();
  const { translate } = useTranslation();

  const [currentPanel, setCurrentPanel] = useState(actions[ShareLinkPanel.WHATSAPP]);

  const [whatsappMessage, setWhatsappMessage] = useState<string>('');
  const [emails, setEmails] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (currentPanel.panel === ShareLinkPanel.WHATSAPP) {
      const whatsAppURL = getWhatsAppLink({ message: window.encodeURIComponent(whatsappMessage) });
      window.open(whatsAppURL, '_blank');
      return;
    }

    if (currentPanel.panel === ShareLinkPanel.COPY_LINK) {
      try {
        await navigator.clipboard.writeText(paymentUrl);
        snackbarStore.handleMessage({
          type: 'success',
          message: translate(LangEnum.PAYMENT_LINK_COPIED_SUCCESS),
        });
      } catch (_) {
        snackbarStore.handleMessage({
          type: 'error',
          message: translate(LangEnum.GLOBAL_ERROR_MESSAGE),
        });
      }
      return;
    }

    if (currentPanel.panel === ShareLinkPanel.EMAIL) {
      setIsSubmitting(true);
      const response = await ordersRepository(orderId).sendPaymentLinkEmails(emails);
      setIsSubmitting(false);

      if (response.ok) {
        snackbarStore.handleMessage({
          type: 'success',
          message: translate(LangEnum.PAYMENT_LINK_SHARED_SUCCESS),
        });
        return;
      }

      snackbarStore.handleMessage({
        type: 'error',
        message: translate(LangEnum.GLOBAL_ERROR_MESSAGE),
      });
      return;
    }
  };

  const handleChangePanel = (_: React.SyntheticEvent, newValue: ShareLinkPanel) => {
    setCurrentPanel(actions[newValue]);
  };

  const disabled =
    isSubmitting || (currentPanel.panel === ShareLinkPanel.EMAIL && emails.length === 0);

  useEffect(() => {
    const newDefaultMessage = `${translate(LangEnum.PAYMENT_LINK_ACCESS_MESSAGE, {
      orderCode,
      brandName: state.brand?.name || state.user?.brand?.name,
    })} ${paymentUrl}`;
    setWhatsappMessage(newDefaultMessage);
    setEmails([]);
  }, [orderCode, paymentUrl, translate, state.brand?.name, state.user?.brand?.name]);

  return {
    emails,
    currentPanel,
    disabled,
    whatsappMessage,
    onSubmit: handleSubmit,
    onChangeEmails: setEmails,
    onChangePanel: handleChangePanel,
    onChangeWhatsappMessage: setWhatsappMessage,
  };
};

export default useOrderSharePaymentModal;
